/**
 * ConnectionManager - Manages visual and audio connections between components
 */

import { Connection } from '../core/Connection';
import { CanvasConnection } from './Connection';
import { CanvasComponent } from './CanvasComponent';
import { eventBus } from '../core/EventBus';
import { EventType, SignalType } from '../core/types';
import { StepSequencer } from '../components/utilities/StepSequencer';
import { KeyboardInput } from '../components/utilities/KeyboardInput';
import { Arpeggiator } from '../components/utilities/Arpeggiator';
import { PolyOscillator } from '../components/generators/PolyOscillator';
import { PolyADSR } from '../components/processors/PolyADSR';
import { PolyVCA } from '../components/processors/PolyVCA';

/**
 * Manages all connections in the patch
 */
export class ConnectionManager {
  private connections: Map<string, Connection>;
  private visualConnections: Map<string, CanvasConnection>;
  private components: Map<string, CanvasComponent>;

  constructor() {
    this.connections = new Map();
    this.visualConnections = new Map();
    this.components = new Map();
  }

  /**
   * Register a component with the manager
   */
  registerComponent(component: CanvasComponent): void {
    this.components.set(component.id, component);
  }

  /**
   * Unregister a component and remove all its connections
   */
  unregisterComponent(componentId: string): void {
    // Remove all connections involving this component
    const connectionsToRemove = Array.from(this.connections.values()).filter(
      (conn) =>
        conn.sourceComponentId === componentId ||
        conn.targetComponentId === componentId
    );

    connectionsToRemove.forEach((conn) => this.removeConnection(conn.id));
    this.components.delete(componentId);
  }

  /**
   * Create a new connection between two ports
   */
  createConnection(
    sourceComponentId: string,
    sourcePortId: string,
    targetComponentId: string,
    targetPortId: string
  ): { success: boolean; error?: string; connectionId?: string } {
    // Get components
    const sourceComponent = this.components.get(sourceComponentId);
    const targetComponent = this.components.get(targetComponentId);

    if (!sourceComponent || !targetComponent) {
      return { success: false, error: 'Component not found' };
    }

    if (!sourceComponent.synthComponent || !targetComponent.synthComponent) {
      return { success: false, error: 'Component not linked to audio' };
    }

    // Get ports
    const sourcePort = sourceComponent.synthComponent.outputs.get(sourcePortId);
    const targetPort = targetComponent.synthComponent.inputs.get(targetPortId);

    if (!sourcePort || !targetPort) {
      return { success: false, error: 'Port not found' };
    }

    // Validate connection
    const validation = Connection.validate(sourcePort.type, targetPort.type);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    // Check if input already has a connection (only one connection per input)
    const existingConnection = Array.from(this.connections.values()).find(
      (conn) =>
        conn.targetComponentId === targetComponentId &&
        conn.targetPortId === targetPortId
    );

    if (existingConnection) {
      // Remove existing connection first
      this.removeConnection(existingConnection.id);
    }

    // Create connection data model
    const connectionId = crypto.randomUUID();
    const connection = new Connection(
      connectionId,
      sourceComponentId,
      sourcePortId,
      targetComponentId,
      targetPortId,
      sourcePort.type
    );

    // Create visual connection
    const sourcePos = sourceComponent.getPortPosition(sourcePortId, false);
    const targetPos = targetComponent.getPortPosition(targetPortId, true);

    if (!sourcePos || !targetPos) {
      return { success: false, error: 'Could not determine port positions' };
    }

    const visualConnection = new CanvasConnection(
      connectionId,
      sourcePos,
      targetPos,
      sourcePort.type
    );

    // Store connections
    this.connections.set(connectionId, connection);
    this.visualConnections.set(connectionId, visualConnection);

    // Connect audio nodes with port IDs
    try {
      sourceComponent.synthComponent.connectTo(
        targetComponent.synthComponent,
        sourcePortId,
        targetPortId
      );
    } catch (error) {
      console.error('Failed to connect audio nodes:', error);
      // Clean up on failure
      this.connections.delete(connectionId);
      this.visualConnections.delete(connectionId);
      return { success: false, error: 'Failed to connect audio nodes' };
    }

    // For arp connections to a StepSequencer from a KeyboardInput, register JS-level
    // getter functions so the sequencer can read live gate/freq without relying on
    // ConstantSourceNode.offset.value (which is not updated by setValueAtTime).
    if (
      targetComponent.synthComponent instanceof StepSequencer &&
      sourceComponent.synthComponent instanceof KeyboardInput
    ) {
      const seq = targetComponent.synthComponent;
      const kbd = sourceComponent.synthComponent;
      if (targetPortId === 'arpeggiate') seq.setArpGateGetter(() => kbd.getGateValue());
      if (targetPortId === 'arpFrequency') seq.setArpFreqGetter(() => kbd.getCurrentFrequency());
    }

    // For connections to an Arpeggiator's cv-in / gate-in, register JS-level getters
    // so the Arpeggiator can read live pitch and gate values from the source component.
    if (targetComponent.synthComponent instanceof Arpeggiator) {
      const arp = targetComponent.synthComponent;
      const src = sourceComponent.synthComponent as any;
      if (targetPortId === 'cv-in') {
        arp.setCvGetter(() => src.getCurrentFrequency?.() ?? 0);
      }
      if (targetPortId === 'gate-in') {
        arp.setGateGetter(() => src.getGateValue?.() ?? 0);
      }
    }

    // For POLY_CV cables: register a VoiceSlots getter on the target so poly components
    // can poll live voice slot data without a Web Audio node (FR-001a, research decision 2).
    if (sourcePort.type === SignalType.POLY_CV) {
      const src = sourceComponent.synthComponent as any;
      const tgt = targetComponent.synthComponent as any;
      if (typeof src.getVoiceSlots === 'function' && typeof tgt.setVoiceSlotsGetter === 'function') {
        tgt.setVoiceSlotsGetter(() => src.getVoiceSlots());
      }
    }

    // POLY_AUDIO: wire PolyOscillator's 4 voice outputs directly into PolyVCA's voice inputs
    if (sourcePort.type === SignalType.POLY_AUDIO) {
      const src = sourceComponent.synthComponent;
      const tgt = targetComponent.synthComponent;
      if (src instanceof PolyOscillator && tgt instanceof PolyVCA) {
        const voiceOutputs = src.getVoiceOutputs();
        tgt.connectPolyAudio(voiceOutputs);
        src.registerPolyAudioConsumer(
          (outs) => tgt.connectPolyAudio(outs),
          ()     => tgt.disconnectPolyAudio(voiceOutputs)
        );
      }
    }

    // POLY_ENV: wire PolyADSR's 4 envelope outputs directly into PolyVCA's voice gain AudioParams
    if (sourcePort.type === SignalType.POLY_ENV) {
      const src = sourceComponent.synthComponent;
      const tgt = targetComponent.synthComponent;
      if (src instanceof PolyADSR && tgt instanceof PolyVCA) {
        const outputGains = src.getOutputGains();
        tgt.connectPolyEnv(outputGains);
        src.registerPolyEnvConsumer(
          (outs) => tgt.connectPolyEnv(outs),
          ()     => tgt.disconnectPolyEnv(outputGains)
        );
      }
    }

    console.log(
      `✅ Connected ${sourceComponent.type}:${sourcePort.name} -> ${targetComponent.type}:${targetPort.name}`
    );

    // Emit for all connections; CV branch also triggers ModulationVisualizer
    eventBus.emit(EventType.CONNECTION_ADDED, {
      connection,
      sourceComponent: sourceComponent.synthComponent,
      targetComponent: targetComponent.synthComponent,
    });

    return { success: true, connectionId };
  }

  /**
   * Remove a connection
   */
  removeConnection(connectionId: string): boolean {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return false;
    }

    // Get components
    const sourceComponent = this.components.get(connection.sourceComponentId);
    const targetComponent = this.components.get(connection.targetComponentId);

    // Disconnect audio nodes with specific port IDs
    if (
      sourceComponent?.synthComponent &&
      targetComponent?.synthComponent
    ) {
      try {
        sourceComponent.synthComponent.disconnectFrom(
          targetComponent.synthComponent,
          connection.sourcePortId,
          connection.targetPortId
        );
      } catch (error) {
        console.error('Failed to disconnect audio nodes:', error);
      }

      // Clear arp source node cache when an arp port is disconnected
      if (targetComponent.synthComponent instanceof StepSequencer) {
        targetComponent.synthComponent.clearArpSources(connection.targetPortId);
      }

      // Clear Arpeggiator getters when its input ports are disconnected
      if (targetComponent.synthComponent instanceof Arpeggiator) {
        const arp = targetComponent.synthComponent;
        if (connection.targetPortId === 'cv-in') arp.clearCvGetter();
        if (connection.targetPortId === 'gate-in') arp.clearGateGetter();
      }

      // Clear POLY_CV getter on target when the poly cable is removed
      if (connection.signalType === SignalType.POLY_CV) {
        const tgt = targetComponent.synthComponent as any;
        if (typeof tgt.clearVoiceSlotsGetter === 'function') {
          tgt.clearVoiceSlotsGetter();
        }
      }

      // Tear down POLY_AUDIO internal wiring
      if (connection.signalType === SignalType.POLY_AUDIO) {
        const src = sourceComponent.synthComponent;
        if (src instanceof PolyOscillator) src.clearPolyAudioConsumer();
      }

      // Tear down POLY_ENV internal wiring
      if (connection.signalType === SignalType.POLY_ENV) {
        const src = sourceComponent.synthComponent;
        if (src instanceof PolyADSR) src.clearPolyEnvConsumer();
      }
    }

    // Notify ModulationVisualizer about CV connection removal
    if (connection.signalType === SignalType.CV) {
      eventBus.emit(EventType.CONNECTION_REMOVED, connectionId);
    }

    // Remove connections
    this.connections.delete(connectionId);
    this.visualConnections.delete(connectionId);

    console.log('🔌 Connection removed:', connectionId);

    return true;
  }

  /**
   * Get connection at a specific position (for click detection)
   */
  getConnectionAt(x: number, y: number): string | null {
    for (const [id, visualConn] of this.visualConnections.entries()) {
      if (visualConn.containsPoint(x, y)) {
        return id;
      }
    }
    return null;
  }

  /**
   * Update visual connection positions (called when components move)
   */
  updateConnectionPositions(componentId: string): void {
    const component = this.components.get(componentId);
    if (!component) return;

    // Update all connections involving this component
    for (const [id, connection] of this.connections.entries()) {
      const visualConnection = this.visualConnections.get(id);
      if (!visualConnection) continue;

      // Update source position
      if (connection.sourceComponentId === componentId) {
        const sourcePos = component.getPortPosition(
          connection.sourcePortId,
          false
        );
        if (sourcePos) {
          visualConnection.updateSource(sourcePos);
        }
      }

      // Update target position
      if (connection.targetComponentId === componentId) {
        const targetPos = component.getPortPosition(
          connection.targetPortId,
          true
        );
        if (targetPos) {
          visualConnection.updateTarget(targetPos);
        }
      }
    }
  }

  /**
   * Render all connections
   */
  render(ctx: CanvasRenderingContext2D): void {
    for (const visualConnection of this.visualConnections.values()) {
      visualConnection.render(ctx);
    }
  }

  /**
   * Get all connections
   */
  getConnections(): Connection[] {
    return Array.from(this.connections.values());
  }

  /**
   * Clear all connections
   */
  clear(): void {
    // Disconnect all audio nodes first
    for (const connection of this.connections.values()) {
      const sourceComponent = this.components.get(connection.sourceComponentId);
      const targetComponent = this.components.get(connection.targetComponentId);

      if (sourceComponent?.synthComponent && targetComponent?.synthComponent) {
        try {
          sourceComponent.synthComponent.disconnectFrom(
            targetComponent.synthComponent
          );
        } catch (error) {
          console.error('Failed to disconnect audio nodes:', error);
        }
        if (targetComponent.synthComponent instanceof StepSequencer) {
          targetComponent.synthComponent.clearArpSources();
        }
      }
    }

    this.connections.clear();
    this.visualConnections.clear();
  }

  /**
   * Get connection count
   */
  getCount(): number {
    return this.connections.size;
  }

  /**
   * Serialize all connections for patch save
   */
  serialize(): any[] {
    return Array.from(this.connections.values()).map((conn) =>
      conn.serialize()
    );
  }

  /**
   * Deserialize connections from patch data
   */
  deserialize(data: any[]): void {
    this.clear();

    for (const connData of data) {
      const connection = Connection.deserialize(connData);

      // Recreate connection
      this.createConnection(
        connection.sourceComponentId,
        connection.sourcePortId,
        connection.targetComponentId,
        connection.targetPortId
      );
    }
  }
}
